import { Modal, cn } from "../shared/ui/index.ts";
import * as React from "react";
import { appOpenPatient } from "../core/app-bridge.ts";
import { Interactions } from "../core/providers/interactions.provider.tsx";
import { usePractice } from "../core/providers/practice.provider.tsx";
import type { Patient } from "../core/services/patients.service.ts";
import {
  patientsService,
  stubPatient,
} from "../core/services/patients.service.ts";
import { I } from "../shared/icons/icons.tsx";
import { useNarrowLayout } from "../core/hooks/use-narrow-layout.ts";
import { useAppShell } from "../core/shell/app-shell.component.tsx";
import {
  pg,
  pgBarActions,
  shellIconBtn,
  toolbarScrollRow,
} from "../shared/page-layout.ts";
import {
  DateStepper,
  OptionStepper,
} from "../shared/selection-nav.component.tsx";
import { Select } from "../shared/select.component.tsx";
import {
  AGENDA_APPOINTMENTS,
  AGENDA_CHAIRS,
  AGENDA_PRACTITIONERS,
  DEFAULT_AGENDA_ACTS,
  appointmentView,
  type AgendaAct,
  type AgendaAppointment,
  type AgendaChair,
  type AgendaPractitioner,
} from "./appointments.ts";
import {
  addDays,
  addMonths,
  formatHour,
  isSameDay,
  isSameMonth,
  startOfWeekMonday,
  toISODate,
  todayLocal,
} from "./date-utils.ts";
import { DayScheduler } from "./day-scheduler.component.tsx";
import { WeekScheduler } from "./week-scheduler.component.tsx";

interface AgendaSettings {
  startHour: number;
  endHour: number;
  pauseEnabled: boolean;
  breakStart: number;
  breakEnd: number;
  showWeekends: boolean;
  cellMinutes: number;
  visibleMonths: number;
}

function fallbackPatientFromEvent(e: {
  patient: string;
  motif: string;
  color: string;
}): Patient {
  return stubPatient({
    uuid: "00000000-0000-4000-8000-000000000099",
    name: e.patient,
    age: 0,
    sex: "M",
    phone: "—",
    last: "—",
    tag: e.motif,
    color: e.color,
  });
}

export function AgendaPage() {
  const { practice } = usePractice();
  const narrow = useNarrowLayout();
  const { mobileNavOpen, sidebarCollapsed: globalSidebarCollapsed } =
    useAppShell();
  const [view, setView] = React.useState<"day" | "week">("week");
  const [selectedDate, setSelectedDate] = React.useState(() => todayLocal());
  const [sidePanelCollapsed, setSidePanelCollapsed] = React.useState(true);

  // Quand la sidebar globale apparaît / se déploie, on ferme le panneau de
  // config agenda pour ne jamais avoir les deux ouverts en même temps.
  const prevMobileNavRef = React.useRef(mobileNavOpen);
  const prevGlobalCollapsedRef = React.useRef(globalSidebarCollapsed);
  React.useEffect(() => {
    const openedMobileNav = !prevMobileNavRef.current && mobileNavOpen;
    const expandedDesktop =
      prevGlobalCollapsedRef.current && !globalSidebarCollapsed;
    if (openedMobileNav || expandedDesktop) {
      setSidePanelCollapsed(true);
    }
    prevMobileNavRef.current = mobileNavOpen;
    prevGlobalCollapsedRef.current = globalSidebarCollapsed;
  }, [mobileNavOpen, globalSidebarCollapsed]);
  const [acts, setActs] = React.useState<AgendaAct[]>(DEFAULT_AGENDA_ACTS);
  const [selectedChairIds, setSelectedChairIds] = React.useState(() =>
    AGENDA_CHAIRS.map((chair) => chair.id),
  );
  const [selectedPractitionerIds, setSelectedPractitionerIds] = React.useState(
    () => AGENDA_PRACTITIONERS.map((practitioner) => practitioner.id),
  );
  const [selectedActIds, setSelectedActIds] = React.useState(() =>
    DEFAULT_AGENDA_ACTS.map((act) => act.id),
  );
  const [settings, setSettings] = React.useState<AgendaSettings>({
    startHour: 8,
    endHour: 18,
    pauseEnabled: true,
    breakStart: 12,
    breakEnd: 14,
    showWeekends: false,
    cellMinutes: 10,
    visibleMonths: 2,
  });
  const [draggingActId, setDraggingActId] = React.useState<string | null>(null);
  const [appointmentEdits, setAppointmentEdits] = React.useState<
    Record<
      string,
      Partial<
        Pick<
          AgendaAppointment,
          | "date"
          | "start"
          | "dur"
          | "chairId"
          | "practitionerId"
          | "actId"
          | "patient"
          | "notes"
        >
      >
    >
  >({});
  const [customAppointments, setCustomAppointments] = React.useState<
    AgendaAppointment[]
  >([]);
  const [deletedAppointmentIds, setDeletedAppointmentIds] = React.useState<
    string[]
  >([]);
  const currentDay = todayLocal();
  const dialog = Interactions.useDialog() as {
    openNewRdv: (
      defaults?:
        | string
        | {
            mode?: "create" | "edit";
            patient?: string;
            motif?: string;
            actId?: string;
            date?: string;
            time?: string;
            duration?: number;
            chairId?: string;
            practitionerId?: string;
            notes?: string;
            acts?: {
              id: string;
              label: string;
              duration: number;
              type?: string;
            }[];
            chairs?: { id: string; label: string; shortLabel: string }[];
            practitioners?: { id: string; name: string; initials: string }[];
            onSubmit?: (values: {
              patient: string;
              actId: string;
              date: string;
              time: string;
              duration: number;
              chairId: string;
              practitionerId: string;
              notes: string;
            }) => void;
            onDelete?: () => void;
          },
    ) => void;
  } | null;

  React.useEffect(() => {
    if (narrow) setView("day");
  }, [narrow]);

  // Sur étroit, on ferme le panneau de config par défaut.
  // Sur desktop, on l'affiche d'office (le toggle l'ouvre/ferme).
  React.useEffect(() => {
    setSidePanelCollapsed(narrow);
  }, [narrow]);

  const navigate = (delta: number) => {
    setSelectedDate((date) => {
      if (view === "day") return addDays(date, delta);
      return addDays(date, delta * 7);
    });
  };
  const today = () => setSelectedDate(todayLocal());
  const allVisibleChairs = AGENDA_CHAIRS.filter((chair) =>
    selectedChairIds.includes(chair.id),
  );

  const appointmentDialogDefaults = (appointment: AgendaAppointment) => {
    const details = appointmentView(appointment, acts);
    return {
      mode: "edit" as const,
      patient: appointment.patient,
      motif: details.motif,
      actId: appointment.actId,
      date: appointment.date,
      time: formatHour(appointment.start),
      duration: Math.round(appointment.dur * 60),
      chairId: appointment.chairId,
      practitionerId: appointment.practitionerId,
      notes: appointment.notes,
      acts,
      chairs: AGENDA_CHAIRS,
      practitioners: AGENDA_PRACTITIONERS,
      onSubmit: (values: {
        patient: string;
        actId: string;
        date: string;
        time: string;
        duration: number;
        chairId: string;
        practitionerId: string;
        notes: string;
      }) => {
        setAppointmentEdits((edits) => ({
          ...edits,
          [appointment.id]: {
            ...edits[appointment.id],
            patient: values.patient,
            actId: values.actId,
            date: values.date,
            start: timeToHour(values.time),
            dur: values.duration / 60,
            chairId: values.chairId,
            practitionerId: values.practitionerId,
            notes: values.notes,
          },
        }));
      },
      onDelete: () => {
        setDeletedAppointmentIds((ids) =>
          ids.includes(appointment.id) ? ids : [...ids, appointment.id],
        );
      },
    };
  };

  const openCreateAppointment = (
    defaults: Partial<
      Pick<AgendaAppointment, "date" | "start" | "dur" | "chairId" | "actId">
    > = {},
  ) => {
    const act =
      acts.find((candidate) => candidate.id === defaults.actId) ?? acts[0];
    const chairId =
      defaults.chairId ?? allVisibleChairs[0]?.id ?? AGENDA_CHAIRS[0]!.id;
    const practitionerId =
      selectedPractitionerIds[0] ?? AGENDA_PRACTITIONERS[0]!.id;
    const date = defaults.date ?? toISODate(selectedDate);
    const start = defaults.start ?? settings.startHour;
    const duration = Math.round(
      (defaults.dur ?? (act?.duration ?? 30) / 60) * 60,
    );
    dialog?.openNewRdv({
      mode: "create",
      actId: act?.id,
      date,
      time: formatHour(start),
      duration,
      chairId,
      practitionerId,
      acts,
      chairs: AGENDA_CHAIRS,
      practitioners: AGENDA_PRACTITIONERS,
      onSubmit: (values) => {
        const matched =
          patientsService.findByName(values.patient, practice.uuid) ??
          patientsService.list(practice.uuid)[0];
        setCustomAppointments((appointments) => [
          ...appointments,
          {
            id: `custom-rdv-${Date.now()}`,
            practiceId: practice.uuid,
            patientUuid:
              matched?.uuid ?? "00000000-0000-4000-8000-000000000001",
            patient: values.patient,
            date: values.date,
            start: timeToHour(values.time),
            dur: values.duration / 60,
            chairId: values.chairId,
            practitionerId: values.practitionerId,
            actId: values.actId,
            notes: values.notes,
          },
        ]);
      },
    });
  };

  const filteredAppointments = React.useMemo(
    () =>
      AGENDA_APPOINTMENTS.map((appointment) => ({
        ...appointment,
        ...appointmentEdits[appointment.id],
      }))
        .concat(customAppointments)
        .filter(
          (appointment) =>
            !deletedAppointmentIds.includes(appointment.id) &&
            selectedChairIds.includes(appointment.chairId) &&
            selectedPractitionerIds.includes(appointment.practitionerId) &&
            selectedActIds.includes(appointment.actId) &&
            appointment.practiceId === practice.uuid,
        ),
    [
      appointmentEdits,
      customAppointments,
      deletedAppointmentIds,
      selectedActIds,
      selectedChairIds,
      selectedPractitionerIds,
      practice.uuid,
    ],
  );

  // Range label — version courte sur narrow (mobile/tablette) pour éviter
  // que le titre déborde et pousse le reste de la toolbar.
  let rangeLabel: string;
  if (view === "week") {
    const start = startOfWeekMonday(selectedDate);
    const end = addDays(start, 6);
    rangeLabel = formatWeekRange(start, end);
  } else if (narrow) {
    rangeLabel = selectedDate.toLocaleDateString("fr-FR", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } else {
    rangeLabel = selectedDate.toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }

  const openPatientFromEvent = (appointment: AgendaAppointment) => {
    const details = appointmentView(appointment, acts);
    const patient =
      patientsService.findByUuid(appointment.patientUuid, practice.uuid) ??
      fallbackPatientFromEvent({
        patient: appointment.patient,
        motif: details.motif,
        color: details.color,
      });
    appOpenPatient(patient);
  };

  const editAppointment = (appointment: AgendaAppointment) => {
    dialog?.openNewRdv(appointmentDialogDefaults(appointment));
  };

  const proposeAppointmentChange = (
    appointment: AgendaAppointment,
    change: Partial<
      Pick<AgendaAppointment, "date" | "start" | "dur" | "chairId">
    >,
  ) => {
    dialog?.openNewRdv(
      appointmentDialogDefaults({ ...appointment, ...change }),
    );
  };

  return (
    <div className={pg.page}>
      <div className={pg.toolbar}>
        <div className={toolbarScrollRow}>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => setSidePanelCollapsed((v) => !v)}
              className={cn(shellIconBtn, "focus-ring")}
              title={
                sidePanelCollapsed
                  ? "Afficher le panneau"
                  : "Masquer le panneau"
              }
              aria-label={
                sidePanelCollapsed
                  ? "Afficher le panneau"
                  : "Masquer le panneau"
              }
              aria-pressed={!sidePanelCollapsed}
            >
              <I.PanelLeft size={18} panelOpen={!sidePanelCollapsed} />
            </button>
            <div aria-hidden className="h-5 w-px shrink-0 bg-sf-border" />
            <DateStepper
              onPrev={() => navigate(-1)}
              onNext={() => navigate(1)}
              onToday={today}
              todayActive={isSameDay(selectedDate, currentDay)}
            />
            <span className="ml-4 shrink-0 whitespace-nowrap text-sm font-semibold capitalize sm:ml-5">
              {rangeLabel}
            </span>
          </div>
          <div className="hidden min-w-0 flex-1 lg:flex">
            <QuickDateStrip
              selectedDate={selectedDate}
              onSelect={(date) => {
                setSelectedDate(date);
              }}
            />
          </div>
          {!narrow && (
            <OptionStepper
              className={pgBarActions}
              ariaLabel="Vue agenda"
              value={view}
              onChange={(v) => setView(v as "day" | "week")}
              options={[
                { value: "day", label: "Jour" },
                { value: "week", label: "Semaine" },
              ]}
            />
          )}
        </div>
      </div>
      <div className={pg.main}>
        <div className={cn(pg.mainRow, "relative")}>
          {!sidePanelCollapsed && narrow && (
            <button
              type="button"
              aria-label="Fermer le panneau"
              onClick={() => setSidePanelCollapsed(true)}
              className="absolute inset-0 z-30 cursor-default border-0 bg-gray-900/35 backdrop-blur-[1px]"
            />
          )}
          {!sidePanelCollapsed && (
            <AgendaSidePanel
              narrow={narrow}
              onClose={() => setSidePanelCollapsed(true)}
              selectedDate={selectedDate}
              onSelectDate={(date) => {
                setSelectedDate(date);
              }}
              chairs={AGENDA_CHAIRS}
              practitioners={AGENDA_PRACTITIONERS}
              acts={acts}
              selectedChairIds={selectedChairIds}
              selectedPractitionerIds={selectedPractitionerIds}
              selectedActIds={selectedActIds}
              onToggleChair={(id) =>
                setSelectedChairIds((ids) => toggleId(ids, id))
              }
              onAllChairs={() =>
                setSelectedChairIds(AGENDA_CHAIRS.map((chair) => chair.id))
              }
              onNoChairs={() => setSelectedChairIds([])}
              onTogglePractitioner={(id) =>
                setSelectedPractitionerIds((ids) => toggleId(ids, id))
              }
              onAllPractitioners={() =>
                setSelectedPractitionerIds(
                  AGENDA_PRACTITIONERS.map((practitioner) => practitioner.id),
                )
              }
              onNoPractitioners={() => setSelectedPractitionerIds([])}
              onToggleAct={(id) =>
                setSelectedActIds((ids) => toggleId(ids, id))
              }
              onAllActs={() => setSelectedActIds(acts.map((act) => act.id))}
              onNoActs={() => setSelectedActIds([])}
              settings={settings}
              onSettingsChange={setSettings}
              onCreateAct={(act) => {
                setActs((current) => [...current, act]);
                setSelectedActIds((ids) => [...ids, act.id]);
              }}
              onDeleteAct={(id) => {
                setActs((current) => current.filter((act) => act.id !== id));
                setSelectedActIds((ids) =>
                  ids.filter((candidate) => candidate !== id),
                );
              }}
              onActDragStart={setDraggingActId}
              onActDragEnd={() => setDraggingActId(null)}
            />
          )}
          <div className="flex min-h-0 min-w-0 flex-1">
            {view === "week" && (
              <WeekScheduler
                selectedDate={selectedDate}
                appointments={filteredAppointments}
                acts={acts}
                chairs={AGENDA_CHAIRS.filter((chair) =>
                  selectedChairIds.includes(chair.id),
                )}
                settings={settings}
                draggingActId={draggingActId}
                onEventClick={editAppointment}
                onEventDoubleClick={openPatientFromEvent}
                onCreateAppointment={openCreateAppointment}
                onActDrop={openCreateAppointment}
                onEventChangeRequest={proposeAppointmentChange}
              />
            )}
            {view === "day" && (
              <DayScheduler
                selectedDate={selectedDate}
                appointments={filteredAppointments}
                acts={acts}
                chairs={AGENDA_CHAIRS.filter((chair) =>
                  selectedChairIds.includes(chair.id),
                )}
                settings={settings}
                draggingActId={draggingActId}
                onEventClick={editAppointment}
                onEventDoubleClick={openPatientFromEvent}
                onCreateAppointment={openCreateAppointment}
                onActDrop={openCreateAppointment}
                onEventChangeRequest={proposeAppointmentChange}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AgendaSidePanel({
  narrow,
  onClose,
  selectedDate,
  onSelectDate,
  chairs,
  practitioners,
  acts,
  selectedChairIds,
  selectedPractitionerIds,
  selectedActIds,
  onToggleChair,
  onAllChairs,
  onNoChairs,
  onTogglePractitioner,
  onAllPractitioners,
  onNoPractitioners,
  onToggleAct,
  onAllActs,
  onNoActs,
  settings,
  onSettingsChange,
  onCreateAct,
  onDeleteAct,
  onActDragStart,
  onActDragEnd,
}: {
  narrow: boolean;
  onClose: () => void;
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
  chairs: AgendaChair[];
  practitioners: AgendaPractitioner[];
  acts: AgendaAct[];
  selectedChairIds: string[];
  selectedPractitionerIds: string[];
  selectedActIds: string[];
  onToggleChair: (id: string) => void;
  onAllChairs: () => void;
  onNoChairs: () => void;
  onTogglePractitioner: (id: string) => void;
  onAllPractitioners: () => void;
  onNoPractitioners: () => void;
  onToggleAct: (id: string) => void;
  onAllActs: () => void;
  onNoActs: () => void;
  settings: AgendaSettings;
  onSettingsChange: (settings: AgendaSettings) => void;
  onCreateAct: (act: AgendaAct) => void;
  onDeleteAct: (id: string) => void;
  onActDragStart: (id: string) => void;
  onActDragEnd: () => void;
}) {
  const [actLabel, setActLabel] = React.useState("");
  const [actColor, setActColor] = React.useState("#7c3aed");
  const [baseMonth, setBaseMonth] = React.useState(
    () => new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1),
  );
  const months = Array.from({ length: settings.visibleMonths }, (_, index) =>
    addMonths(baseMonth, index),
  );

  const updateSetting = <K extends keyof AgendaSettings>(
    key: K,
    value: AgendaSettings[K],
  ) => onSettingsChange({ ...settings, [key]: value });

  const createAct = () => {
    const label = actLabel.trim();
    if (!label) return;
    onCreateAct({
      id: `custom-${Date.now()}`,
      label,
      color: actColor,
      duration: settings.cellMinutes,
      type: "technique",
    });
    setActLabel("");
  };

  return (
    <aside
      className={cn(
        "flex min-h-0 flex-col overflow-hidden border-sf-border bg-white",
        narrow
          ? "absolute inset-y-0 left-0 z-40 w-[min(86vw,320px)] border-r shadow-(--sf-shadow-lg) animate-[sf-slide-in-left_180ms_cubic-bezier(0.2,0.8,0.2,1)]"
          : "w-75 shrink-0 rounded-lg border",
      )}
    >
      {narrow && (
        <div className="flex items-center justify-between border-b border-sf-border px-3 py-2">
          <span className="text-[13px] font-semibold text-sf-text">
            Configuration
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer le panneau"
            className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded border-0 bg-transparent text-sf-text-muted hover:bg-gray-100 hover:text-sf-text"
          >
            <I.X size={14} />
          </button>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto p-2.5 scrollbar-none [&::-webkit-scrollbar]:hidden">
        <div className="mb-1.5 flex items-center justify-between">
          <button
            type="button"
            onClick={() =>
              setBaseMonth((m) => addMonths(m, -settings.visibleMonths))
            }
            className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-sf-surface-alt"
          >
            <I.ChevronLeft size={13} />
          </button>
          <button
            type="button"
            onClick={() =>
              setBaseMonth(
                new Date(
                  todayLocal().getFullYear(),
                  todayLocal().getMonth(),
                  1,
                ),
              )
            }
            className="text-[10.5px] font-semibold text-sf-primary hover:underline"
          >
            Aujourd'hui
          </button>
          <button
            type="button"
            onClick={() =>
              setBaseMonth((m) => addMonths(m, settings.visibleMonths))
            }
            className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-sf-surface-alt"
          >
            <I.ChevronRight size={13} />
          </button>
        </div>
        <div
          className={cn(
            "grid gap-2.5",
            settings.visibleMonths === 1 ? "grid-cols-1" : "grid-cols-2",
          )}
        >
          {months.map((month) => (
            <MiniMonth
              key={`${month.getFullYear()}-${month.getMonth()}`}
              month={month}
              selectedDate={selectedDate}
              onSelectDate={onSelectDate}
            />
          ))}
        </div>

        <div className="mt-3 border-t border-sf-border/80 pt-2.5">
          <FilterGroup
            title="Fauteuils"
            items={chairs.map((chair) => ({
              id: chair.id,
              label: chair.shortLabel,
              color: "#64748b",
            }))}
            selectedIds={selectedChairIds}
            onToggle={onToggleChair}
            onAll={onAllChairs}
            onNone={onNoChairs}
          />
          <FilterGroup
            title="Praticiens"
            items={practitioners.map((practitioner) => ({
              id: practitioner.id,
              label: practitioner.initials,
              color: practitioner.color,
            }))}
            selectedIds={selectedPractitionerIds}
            onToggle={onTogglePractitioner}
            onAll={onAllPractitioners}
            onNone={onNoPractitioners}
          />
          <ActFilterGroup
            acts={acts}
            selectedIds={selectedActIds}
            onToggle={onToggleAct}
            actLabel={actLabel}
            actColor={actColor}
            onActLabelChange={setActLabel}
            onActColorChange={setActColor}
            onCreateAct={createAct}
            onDeleteAct={onDeleteAct}
            onAll={onAllActs}
            onNone={onNoActs}
            onActDragStart={onActDragStart}
            onActDragEnd={onActDragEnd}
          />
        </div>

        <div className="mt-3 border-t border-sf-border/80 pt-2.5">
          <div className="space-y-2 text-xs">
            <div className="block">
              <span className="font-semibold">Horaires d'une journée</span>
              <div className="mt-1 grid grid-cols-2 gap-1.5">
                <Select
                  value={String(settings.startHour)}
                  onChange={(v) => updateSetting("startHour", Number(v))}
                  options={Array.from({ length: 24 }, (_, hour) => ({
                    value: String(hour),
                    label: `${String(hour).padStart(2, "0")}:00`,
                  }))}
                />
                <Select
                  value={String(settings.endHour)}
                  onChange={(v) => updateSetting("endHour", Number(v))}
                  options={Array.from({ length: 24 }, (_, hour) => ({
                    value: String(hour + 1),
                    label: `${String(hour + 1).padStart(2, "0")}:00`,
                  }))}
                />
              </div>
            </div>
            <label className="flex items-center gap-2 font-semibold">
              <input
                type="checkbox"
                checked={settings.showWeekends}
                onChange={(event) =>
                  updateSetting("showWeekends", event.target.checked)
                }
                className="size-4 accent-sf-primary"
              />
              Afficher les weekends
            </label>
            <label className="flex items-center gap-2 font-semibold">
              <input
                type="checkbox"
                checked={settings.pauseEnabled}
                onChange={(event) =>
                  updateSetting("pauseEnabled", event.target.checked)
                }
                className="size-4 accent-sf-primary"
              />
              Pause journalière
            </label>
            <div
              className={cn("block", !settings.pauseEnabled && "opacity-50")}
            >
              <div className="mt-1 grid grid-cols-2 gap-1.5">
                <Select
                  value={String(settings.breakStart)}
                  disabled={!settings.pauseEnabled}
                  onChange={(v) => updateSetting("breakStart", Number(v))}
                  options={Array.from({ length: 24 }, (_, hour) => ({
                    value: String(hour),
                    label: `${String(hour).padStart(2, "0")}:00`,
                  }))}
                />
                <Select
                  value={String(settings.breakEnd)}
                  disabled={!settings.pauseEnabled}
                  onChange={(v) => updateSetting("breakEnd", Number(v))}
                  options={Array.from({ length: 24 }, (_, hour) => ({
                    value: String(hour + 1),
                    label: `${String(hour + 1).padStart(2, "0")}:00`,
                  }))}
                />
              </div>
            </div>
            <div className="block">
              <span className="font-semibold">Taille de cellule</span>
              <div className="mt-1">
                <Select
                  value={String(settings.cellMinutes)}
                  onChange={(v) => updateSetting("cellMinutes", Number(v))}
                  options={[5, 6, 10, 15, 20, 30, 60].map((value) => ({
                    value: String(value),
                    label: value === 60 ? "1h" : `${value} minutes`,
                  }))}
                />
              </div>
            </div>
            <div className="block">
              <span className="font-semibold">Nombre de mois visibles</span>
              <div className="mt-1">
                <Select
                  value={String(settings.visibleMonths)}
                  onChange={(v) => updateSetting("visibleMonths", Number(v))}
                  options={[1, 2, 4].map((value) => ({
                    value: String(value),
                    label: String(value),
                  }))}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}

function MiniMonth({
  month,
  selectedDate,
  onSelectDate,
}: {
  month: Date;
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
}) {
  const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = startOfWeekMonday(monthStart);
  const days = Array.from({ length: 42 }, (_, index) => addDays(start, index));
  const today = todayLocal();

  return (
    <div className="rounded-lg bg-sf-surface-alt/70 p-2 ring-1 ring-sf-border/70">
      <div className="mb-1.5 text-center text-xs font-bold capitalize text-sf-text">
        {monthStart.toLocaleDateString("fr-FR", {
          month: "short",
          year: "numeric",
        })}
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] text-sf-text-muted">
        {["L", "M", "M", "J", "V", "S", "D"].map((day, index) => (
          <div key={`${day}-${index}`} className="font-semibold">
            {day}
          </div>
        ))}
        {days.map((day) => {
          const currentMonth = isSameMonth(day, monthStart);
          const selected = isSameDay(day, selectedDate);
          return (
            <button
              key={toISODate(day)}
              type="button"
              onClick={() => onSelectDate(day)}
              className={cn(
                "h-5 rounded-md text-[10px] font-medium",
                selected
                  ? "bg-sf-primary text-white"
                  : isSameDay(day, today)
                    ? "bg-sf-primary-50 text-sf-primary"
                    : "hover:bg-gray-50",
                !currentMonth && "text-sf-text-faint",
              )}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FilterGroup({
  title,
  items,
  selectedIds,
  onToggle,
  onAll,
  onNone,
}: {
  title: string;
  items: { id: string; label: string; color: string }[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  onAll: () => void;
  onNone: () => void;
}) {
  return (
    <div className="mb-2.5">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <div className="text-[11px] font-semibold text-sf-text">{title}</div>
        <AllNoneButtons onAll={onAll} onNone={onNone} />
      </div>
      <div className="flex flex-wrap gap-1">
        {items.map((item) => {
          const selected = selectedIds.includes(item.id);
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onToggle(item.id)}
              className={cn(
                "inline-flex h-6 items-center gap-1 rounded-md border px-1.5 text-[11px] font-medium",
                selected
                  ? "border-sf-primary-100 bg-sf-primary-50 text-sf-text"
                  : "border-sf-border bg-white text-sf-text-muted",
              )}
            >
              <span
                className="size-2 rounded-full"
                style={{ background: item.color }}
              />
              {item.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ActFilterGroup({
  acts,
  selectedIds,
  onToggle,
  actLabel,
  actColor,
  onActLabelChange,
  onActColorChange,
  onCreateAct,
  onDeleteAct,
  onAll,
  onNone,
  onActDragStart,
  onActDragEnd,
}: {
  acts: AgendaAct[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  actLabel: string;
  actColor: string;
  onActLabelChange: (label: string) => void;
  onActColorChange: (color: string) => void;
  onCreateAct: () => void;
  onDeleteAct: (id: string) => void;
  onAll: () => void;
  onNone: () => void;
  onActDragStart: (id: string) => void;
  onActDragEnd: () => void;
}) {
  const [open, setOpen] = React.useState(false);

  return (
    <div className="mb-0.5">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <div className="text-[11px] font-semibold text-sf-text">Actes</div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex size-5 items-center justify-center rounded-md border border-sf-border bg-white text-xs font-bold text-sf-text-secondary hover:border-sf-primary-100 hover:text-sf-primary"
            title="Créer un acte"
          >
            +
          </button>
        </div>
        <AllNoneButtons onAll={onAll} onNone={onNone} />
      </div>
      <div className="mb-1.5 text-[10px] font-medium text-sf-text-muted">
        Glisser un acte sur le planning pour créer un RDV.
      </div>
      <div className="flex flex-wrap gap-1">
        {acts.map((act) => {
          const selected = selectedIds.includes(act.id);
          return (
            <span
              key={act.id}
              draggable
              onDragStart={(event) => {
                event.dataTransfer.setData("application/x-agenda-act", act.id);
                event.dataTransfer.effectAllowed = "copy";
                onActDragStart(act.id);
              }}
              onDragEnd={() => onActDragEnd()}
              className={cn(
                "inline-flex h-6 cursor-grab items-center gap-1 rounded-md border pl-1.5 pr-1 text-[11px] font-medium active:cursor-grabbing",
                selected
                  ? "border-sf-primary-100 bg-sf-primary-50 text-sf-text"
                  : "border-sf-border bg-white text-sf-text-muted",
              )}
            >
              <button
                type="button"
                onClick={() => onToggle(act.id)}
                className="inline-flex items-center gap-1"
              >
                <span
                  className="size-2 rounded-full"
                  style={{ background: act.color }}
                />
                {act.label}
              </button>
              <button
                type="button"
                onClick={() => onDeleteAct(act.id)}
                className="ml-0.5 rounded px-0.5 text-sf-text-faint hover:bg-white hover:text-sf-danger"
                title={`Supprimer ${act.label}`}
              >
                ×
              </button>
            </span>
          );
        })}
      </div>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Créer un acte"
        subtitle="Choisissez un nom et une couleur visible dans le planning."
        width={420}
        footer={
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="h-8 rounded border border-sf-border-strong bg-white px-3 text-xs font-semibold text-sf-text"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={() => {
                onCreateAct();
                setOpen(false);
              }}
              className="h-8 rounded bg-sf-primary px-3 text-xs font-semibold text-white"
            >
              Ajouter
            </button>
          </div>
        }
      >
        <div className="space-y-3">
          <input
            value={actLabel}
            onChange={(event) => onActLabelChange(event.target.value)}
            placeholder="Nom de l'acte"
            className="h-9 w-full rounded border border-sf-border-strong bg-white px-3 text-sm outline-none"
          />
          <div>
            <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-sf-text-muted">
              Couleurs proposées
            </div>
            <div className="flex flex-wrap gap-1.5">
              {TAILWIND_ACT_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => onActColorChange(color)}
                  className={cn(
                    "size-7 rounded border",
                    actColor === color
                      ? "border-sf-text ring-2 ring-sf-primary-100"
                      : "border-white",
                  )}
                  style={{ background: color }}
                  title={color}
                />
              ))}
            </div>
          </div>
          <label className="block text-xs font-semibold text-sf-text-secondary">
            Autre couleur
            <input
              type="color"
              value={actColor}
              onChange={(event) => onActColorChange(event.target.value)}
              className="mt-1 h-8 w-full cursor-pointer rounded border border-sf-border bg-white p-0.5"
              title="Autre couleur"
            />
          </label>
        </div>
      </Modal>
    </div>
  );
}

function AllNoneButtons({
  onAll,
  onNone,
}: {
  onAll: () => void;
  onNone: () => void;
}) {
  return (
    <div className="flex items-center gap-1 text-[10.5px] font-semibold">
      <button
        type="button"
        onClick={onAll}
        className="text-sf-primary hover:underline"
      >
        Tous
      </button>
      <span className="text-sf-text-faint">/</span>
      <button
        type="button"
        onClick={onNone}
        className="text-sf-text-muted hover:underline"
      >
        Aucun
      </button>
    </div>
  );
}

function QuickDateStrip({
  selectedDate,
  onSelect,
}: {
  selectedDate: Date;
  onSelect: (date: Date) => void;
}) {
  const today = todayLocal();
  const offsets = [-1, 0, ...Array.from({ length: 15 }, (_, i) => i + 1)];

  return (
    <div className="flex min-w-0 flex-1 items-center justify-end overflow-hidden">
      <div className="flex min-w-0 items-center gap-0.5 px-0.5">
        {offsets.map((offset) => {
          const date = addDays(today, offset);
          const selected = isSameDay(date, selectedDate);
          return (
            <button
              key={offset}
              type="button"
              onClick={() => onSelect(date)}
              title={date.toLocaleDateString("fr-FR", {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
              className={cn(
                quickButtonVisibility(offset),
                "inline-flex h-8 min-w-8 cursor-pointer items-center justify-center rounded-md border px-1.5 text-[11.5px] font-semibold transition-colors",
                selected
                  ? "border-sf-primary bg-sf-primary text-white"
                  : "border-transparent bg-transparent text-sf-text-secondary hover:border-sf-primary-100 hover:bg-sf-primary-50 hover:text-sf-primary",
              )}
            >
              {quickOffsetLabel(offset)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function quickOffsetLabel(offset: number): string {
  if (offset === 0) return "J";
  return offset > 0 ? `+${offset}` : String(offset);
}

function quickButtonVisibility(offset: number): string {
  if (offset >= 9) return "hidden 2xl:inline-flex";
  if (offset >= 5) return "hidden xl:inline-flex";
  return "inline-flex";
}

function toggleId(ids: string[], id: string): string[] {
  return ids.includes(id)
    ? ids.filter((candidate) => candidate !== id)
    : [...ids, id];
}

const TAILWIND_ACT_COLORS = [
  "#4f46e5",
  "#0d9488",
  "#0284c7",
  "#7c3aed",
  "#db2777",
  "#dc2626",
  "#ea580c",
  "#ca8a04",
  "#16a34a",
  "#475569",
];

function formatWeekRange(start: Date, end: Date): string {
  if (
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth()
  ) {
    return `${start.getDate()} – ${end.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })}`;
  }

  if (start.getFullYear() === end.getFullYear()) {
    return `${start.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
    })} – ${end.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })}`;
  }

  return `${start.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  })} – ${end.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  })}`;
}

function timeToHour(time: string): number {
  const [hours = 0, minutes = 0] = time.split(":").map(Number);
  return hours + minutes / 60;
}
